import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { theme } from 'antd';
import dayjs from 'dayjs';
import {
  ProfileLayout,
  UserPostsSection,
  PeopleListSection,
} from '../components/Profile';
import type { ActionSheetItem } from '../components/SpecActionSheet/SpecActionSheet';
import type { CommentProps } from '../components/Comment/Comment';
import type { PersonItem } from '../components/Profile';
import { UserTag } from '../components/UserTag/UserTag';
import type { UserType } from '../components/UserTag/UserTag';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import { DefaultAvatar } from '../components/icons';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import { useShare } from '../hooks/useShare';
import { useAuth } from '../contexts/AuthContext';
import { useFanProfile } from '../api/queries';

const FanProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const { user } = useAuth();
  const isProfileSaved = (location.state as any)?.profileSaved === true;

  // Determine which user to show: URL param or current user
  const targetUid = id || user?.uid;
  const isOwnProfile = !id || id === user?.uid;

  const { data: fanProfile, isLoading, error } = useFanProfile(targetUid);

  const [toastMessage, setToastMessage] = useState<string | null>(isProfileSaved ? 'Profile Saved' : null);
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const share = useShare(setToastMessage);

  useEffect(() => {
    if (isProfileSaved) {
      window.history.replaceState({}, '');
    }
  }, [isProfileSaved]);

  // Format timestamp for comments
  const formatTimestamp = (timestamp: string | { seconds: number } | undefined) => {
    if (!timestamp) return '';
    if (typeof timestamp === 'object' && 'seconds' in timestamp) {
      return dayjs.unix(timestamp.seconds).format('MMM D h:mm A');
    }
    const parsed = dayjs(timestamp);
    if (!parsed.isValid()) return '';
    return parsed.format('MMM D h:mm A');
  };

  // Format date for event info
  const formatDate = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Extract display name from email
  const formatUserName = (name: string | undefined) => {
    if (!name) return 'Anonymous';
    if (name.includes('@')) return name.split('@')[0];
    return name;
  };

  // Transform comments to CommentProps format
  const comments: Omit<CommentProps, 'className'>[] = useMemo(() => {
    if (!fanProfile?.comments) return [];
    return fanProfile.comments.map(comment => ({
      userName: formatUserName(comment.user_name),
      avatarUrl: comment.user_avatar_url,
      timestamp: formatTimestamp(comment.timestamp || comment.created_at),
      userTags: (comment.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      text: comment.comment,
      showEventInfo: !!(comment.band_name && comment.show_date && comment.venue_name),
      eventInfo: (comment.band_name && comment.show_date && comment.venue_name) ? {
        artistName: comment.band_name,
        artistAvatarUrl: comment.band_photo_url,
        date: formatDate(comment.show_date),
        venue: comment.venue_name,
      } : undefined,
    }));
  }, [fanProfile?.comments]);

  // Transform media to photos array
  const photos: string[] = useMemo(() => {
    if (!fanProfile?.media) return [];
    return fanProfile.media.map(m => m.url);
  }, [fanProfile?.media]);

  // Transform contributor bands to PersonItem format
  const contributorBands: PersonItem[] = useMemo(() => {
    if (!fanProfile?.contributorBands) return [];
    return fanProfile.contributorBands.map(band => ({
      name: band.name,
      avatarShape: 'rounded' as const,
      route: `/bands/${band.id}`,
    }));
  }, [fanProfile?.contributorBands]);

  // Determine user tags (musicians don't also show fan tag)
  const userTags: UserType[] = useMemo(() => {
    const tags: UserType[] = [];
    if (fanProfile?.isMusician) {
      tags.push('musician');
    } else if (fanProfile?.isFan) {
      tags.push('fan');
    }
    if (fanProfile?.contributorBands && fanProfile.contributorBands.length > 0) {
      tags.push('showContributor');
    }
    return tags.length > 0 ? tags : ['fan'];
  }, [fanProfile]);

  const displayName = fanProfile?.displayName || 'User';
  const firstName = displayName.split(' ')[0];

  const actionItems: ActionSheetItem[] = [
    { key: 'share', label: 'Share', onClick: () => share({ title: displayName }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); setToastMessage('Link Copied'); } },
    ...(isOwnProfile ? [{ key: 'edit-profile', label: 'Edit Profile', onClick: () => navigate('/edit-fan-profile') }] : []),
  ];

  if (isLoading) {
    return (
      <ProfileLayout profileType="fan" heroVariant="none" actionSheetItems={[]}>
        <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
          Loading profile...
        </div>
      </ProfileLayout>
    );
  }

  if (error || !fanProfile) {
    return (
      <ProfileLayout profileType="fan" heroVariant="none" actionSheetItems={[]}>
        <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorError }}>
          {error?.message || 'User not found'}
        </div>
      </ProfileLayout>
    );
  }

  return (
    <ProfileLayout
      profileType="fan"
      heroVariant="none"
      actionSheetItems={actionItems}
    >
      <div className="band-identity">
        <div className="band-identity__avatar">
          <div className="band-identity__avatar-placeholder band-identity__avatar-placeholder--circle">
            <DefaultAvatar size={48} />
          </div>
        </div>
        <div className="band-identity__info">
          <h1 className="band-identity__name">{displayName}</h1>
          <div className="band-identity__badges">
            {userTags.map(tag => (
              <UserTag key={tag} type={tag} onClick={() => setOpenInfoType(tag)} />
            ))}
          </div>
        </div>
        {userTags.map(tag => (
          <UserTypeInfo key={tag} type={tag} open={openInfoType === tag} onClose={() => setOpenInfoType(null)} />
        ))}
      </div>

      {(comments.length > 0 || photos.length > 0) && (
        <UserPostsSection
          userName={firstName}
          comments={comments}
          photos={photos}
        />
      )}

      {contributorBands.length > 0 && (
        <PeopleListSection
          title="Show Contributor"
          subtitle={`Bands ${firstName} can add shows for as a show contributor`}
          people={contributorBands}
          avatarShape="rounded"
        />
      )}

      <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />
    </ProfileLayout>
  );
};

export default FanProfile;
